/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { functionWithFG } from '@atlaskit/platform-feature-flags-react';
import { Stack } from '@atlaskit/primitives/compiled';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	RequestStatus,
	useBFFDataProvider,
	type ProductLink,
} from '@atlassian/navigation-bff-data-provider';
import {
	ERROR_LOCATION__GLOBAL_SHORTCUTS,
	useNavBoundaryContext,
} from '@atlassian/navigation-error-boundary';

import { ANALYTICS_CHANNEL } from '../../constants';
import type {
	fnGetFilteredProductLinks,
	GlobalShortcutLinkItem,
	GlobalShortcutsProps,
	MappedGlobalShortcutsItem,
} from '../../types';
import { getUrlMaybe } from '../../utils/get-url-maybe';
import { UFO_LOAD_HOLD_NAME } from '../../utils/ufo/constants';
import { DragAndDropOptionsProvider } from '../drag-and-drop-options-provider';
import { ImageFromUrl } from '../image-from-url';
import { MenuSkeletonItem } from '../menu-skeleton-item';
import { ShortcutMenuItem } from '../shortcut-menu-item';
import { ViewAllAppsItem } from '../view-all-apps-item';

export function Menu({
	shortcutItems = [],
	forceStatic,
	cloudId,
	orgId,
	userId,
	product,
	...restProps
}: GlobalShortcutsProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { buildURLAndFetchData, status, error, productLinks } = useBFFDataProvider({
		request: {},
		cloudId,
		orgId,
		userId,
		forceStatic,
		product,
	});

	useEffect(() => {
		if (!forceStatic) {
			buildURLAndFetchData();
		}
	}, [buildURLAndFetchData, forceStatic]);

	const { handleError } = useNavBoundaryContext({
		location: ERROR_LOCATION__GLOBAL_SHORTCUTS,
		sentryConfig: { product },
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		disableAnalytics: true,
	});

	useEffect(() => {
		if (error) {
			handleError(error, {
				componentStack: 'Menu',
			});
		}
	}, [handleError, error]);

	const mountedRef = useRef(false);
	useEffect(() => {
		if (
			forceStatic ||
			status?.toLowerCase() !== RequestStatus.COMPLETE ||
			(productLinks && !Object.keys(productLinks).length) ||
			mountedRef.current === true
		) {
			return;
		}

		mountedRef.current = true;
		const missingShortcuts = shortcutItems.filter((item): item is GlobalShortcutLinkItem => {
			// todo: Should we also include links with missing APS keys? Currently a link could be missing an APS key and not be included in this list.
			return (
				item.type === 'link' && !getUrlMaybe(item) && !!item.apsKey && !productLinks?.[item.apsKey]
			);
		});
		const intended = shortcutItems.length;
		const rendered = intended - missingShortcuts.length;

		createAnalyticsEvent({
			eventType: UI_EVENT_TYPE,
			action: 'cls',
			actionSubject: 'menu',
			actionSubjectId: 'globalNavigation',
			attributes: {
				name: product,
				intended,
				rendered,
				missingAPSKeys: missingShortcuts.map((item) => item.apsKey).filter(Boolean),
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, shortcutItems, productLinks, product, forceStatic, status]);

	return (
		<MenuInner
			shortcutItems={shortcutItems}
			status={status}
			productLinks={productLinks}
			forceStatic={forceStatic}
			{...restProps}
		/>
	);
}

function MenuInner({
	testId,
	enableDragAndDrop,
	forceStatic,
	viewAllAppsConfig,
	product,
	shortcutItems,
	status,
	productLinks,
	productLinksFilter,
	promoteSite,
	onUpdateGetFilteredProductLinks,
	openInSameTab,
}: GlobalShortcutsProps & {
	productLinks: Record<string, ProductLink[]>;
	status: RequestStatus | null;
}) {
	// Update Ref
	const onUpdateGetFilteredProductLinksRef = useRef<typeof onUpdateGetFilteredProductLinks>(
		onUpdateGetFilteredProductLinks,
	);
	onUpdateGetFilteredProductLinksRef.current = onUpdateGetFilteredProductLinks;

	// Filter Ref
	const productLinksFilterRef = useRef<typeof productLinksFilter>(productLinksFilter);
	productLinksFilterRef.current = productLinksFilter;

	/**
	 * Making a stable reference to `productLinks` to avoid `getFilteredProductLinks`
	 * changing and calling `onUpdateGetFilteredProductLinksRef` repeatedly.
	 */
	const productLinksRef = useRef(productLinks);
	useEffect(() => {
		productLinksRef.current = productLinks;
	}, [productLinks]);

	/**
	 * Call `onUpdateGetFilteredProductLinks` with a stable reference to `getFilteredProductLinks`
	 * so that consumers can call it without causing unnecessary re-renders or effects in their components.
	 */
	useEffect(() => {
		onUpdateGetFilteredProductLinksRef.current?.((apsKey: string) =>
			getFilteredProductLinks(
				apsKey,
				productLinks,
				productLinksRef,
				status,
				promoteSite,
				productLinksFilterRef,
			),
		);
	}, [productLinks, status, promoteSite]);

	const mappedShortcutItems = getMappedShortcutItems({
		shortcutItems,
		productLinks,
		forceStatic,
		status,
		promoteSite,
		productLinksRef,
		productLinksFilterRef,
	});

	const shortcutList = (
		<MenuList>
			{mappedShortcutItems?.map((item) =>
				createLinkItem(item, status, enableDragAndDrop, openInSameTab, forceStatic, product),
			)}
			{viewAllAppsConfig?.path && <ViewAllAppsItem {...viewAllAppsConfig} product={product} />}
		</MenuList>
	);

	return (
		<Stack testId={testId}>
			{enableDragAndDrop ? (
				<DragAndDropOptionsProvider enabled={enableDragAndDrop}>
					{shortcutList}
				</DragAndDropOptionsProvider>
			) : (
				shortcutList
			)}
			{!forceStatic && (
				<UFOLoadHold
					name={UFO_LOAD_HOLD_NAME}
					hold={status === RequestStatus.INIT || status === RequestStatus.LOADING}
				/>
			)}
		</Stack>
	);
}

function getMappedShortcutItems({
	shortcutItems = [],
	productLinks,
	forceStatic,
	status,
	promoteSite,
	productLinksRef,
	productLinksFilterRef,
}: {
	forceStatic: boolean | undefined;
	productLinks: Record<string, ProductLink[]>;
	productLinksFilterRef: React.MutableRefObject<
		((item: ProductLink, index: number, arr: ProductLink[]) => boolean) | undefined
	>;
	productLinksRef: React.MutableRefObject<Record<string, ProductLink[]>>;
	promoteSite: boolean | undefined;
	shortcutItems: GlobalShortcutsProps['shortcutItems'];
	status: RequestStatus | null;
}): MappedGlobalShortcutsItem[] {
	return shortcutItems.map((item, index) => {
		let key = (item.type === 'link' && item.apsKey) || `shortcut-${index}`;
		if (item.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
			key = item?.apsKey ? item.apsKey : `shortcut-${index}`;
		}

		// Render nested items if the dynamic data is available and we are not forcing static rendering
		if (
			!forceStatic &&
			status?.toLowerCase() === RequestStatus.COMPLETE &&
			item.type === 'link' &&
			item.apsKey &&
			productLinks?.[item.apsKey]
		) {
			const { links: filteredProductLinks, promotedLink } = getFilteredProductLinks(
				item.apsKey,
				productLinks,
				productLinksRef,
				status,
				promoteSite,
				productLinksFilterRef,
			);
			const siteName = filteredProductLinks?.[0]?.displayName;

			// Determine whether shortcut is a single site and is current site and return link instead of flyout
			if (filteredProductLinks.length === 1 && !item.url) {
				return {
					...item,
					key,
					url: filteredProductLinks[0].url,
				};
			}

			if (filteredProductLinks.length > 1) {
				return {
					...item,
					siteName,
					key,
					type: 'expand',
					promoted: promotedLink
						? {
								type: 'link',
								icon: <ImageFromUrl src={promotedLink.avatar} alt="" fallback={item.icon} />,
								label: promotedLink.displayName,
								url: promotedLink.url,
							}
						: undefined,
					items: filteredProductLinks.map((link) => ({
						type: 'link',
						icon: <ImageFromUrl src={link.avatar} alt="" fallback={item.icon} />,
						label: link.displayName,
						displayName: link.displayName,
						url: link.url,
					})),
				};
			}
		}

		return {
			...item,
			key,
		};
	});
}

function getFilteredProductLinksOld(
	apsKey: string,
	productLinks: Record<string, ProductLink[]> = {},
	productLinksRef: React.MutableRefObject<Record<string, ProductLink[]>>,
	status: RequestStatus | null,
	promoteSite?: boolean,
	productLinksFilterRef?: React.MutableRefObject<
		((item: ProductLink, index: number, arr: ProductLink[]) => boolean) | undefined
	>,
): ReturnType<fnGetFilteredProductLinks> {
	let filteredProductLinks = productLinks[apsKey] ?? [];

	if (productLinksFilterRef?.current) {
		filteredProductLinks = filteredProductLinks.filter(productLinksFilterRef.current);
	}

	return {
		promotedLink: filteredProductLinks.find(
			(item, _i, arr) => arr.length === 1 || (promoteSite && item.isCurrentSite),
		),
		links: filteredProductLinks.filter(
			(item, _i, arr) => arr.length === 1 || !(promoteSite && item.isCurrentSite),
		),
		status,
	};
}

function getFilteredProductLinksNew(
	apsKey: string,
	productLinks: Record<string, ProductLink[]> = {},
	productLinksRef: React.MutableRefObject<Record<string, ProductLink[]>>,
	status: RequestStatus | null,
	promoteSite?: boolean,
	productLinksFilterRef?: React.MutableRefObject<
		((item: ProductLink, index: number, arr: ProductLink[]) => boolean) | undefined
	>,
): ReturnType<fnGetFilteredProductLinks> {
	let filteredProductLinks = productLinksRef.current[apsKey] ?? [];

	if (productLinksFilterRef?.current) {
		filteredProductLinks = filteredProductLinks.filter(productLinksFilterRef.current);
	}

	return {
		promotedLink: filteredProductLinks.find(
			(item, _i, arr) => arr.length === 1 || (promoteSite && item.isCurrentSite),
		),
		links: filteredProductLinks.filter(
			(item, _i, arr) => arr.length === 1 || !(promoteSite && item.isCurrentSite),
		),
		status,
	};
}

function createLinkItem(
	shortcut: MappedGlobalShortcutsItem,
	status: RequestStatus | null,
	enableDragAndDrop?: boolean,
	openInSameTab?: boolean,
	forceStatic?: boolean,
	product?: string,
	useSkeleton?: boolean,
) {
	if (status?.toLowerCase() === RequestStatus.COMPLETE) {
		if (shortcut.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
			return (
				<ShortcutMenuItem
					key={shortcut.key}
					shortcut={shortcut}
					dragAndDropEnabled={enableDragAndDrop}
					status={status}
					isInMoreMenu={false}
				/>
			);
		}

		return (
			<ShortcutMenuItem
				key={shortcut.key}
				shortcut={shortcut}
				openInSameTab={openInSameTab}
				status={status}
				expandItems={
					shortcut.type === 'expand' && (shortcut.items?.length ?? 0 > 1)
						? shortcut.items
						: undefined
				}
				promotedItem={shortcut.type === 'expand' ? shortcut.promoted : undefined}
				siteName={shortcut.type === 'expand' ? shortcut.siteName : undefined}
				product={product}
			/>
		);
	}

	// Show skeleton while BFF is loading
	if (useSkeleton && (status === RequestStatus.LOADING || status === RequestStatus.INIT)) {
		return <MenuSkeletonItem key={shortcut.key} />;
	}

	// We only want to allow static menu expand item if we are forcing static,
	// otherwise it should be showing the singular item as a disabled link until the
	// data is loaded.
	if (forceStatic && shortcut.type === 'expand' && shortcut.items) {
		return (
			<ShortcutMenuItem
				key={shortcut.key}
				shortcut={shortcut}
				expandItems={shortcut.items}
				forceStatic={forceStatic}
				product={product}
				openInSameTab={openInSameTab}
			/>
		);
	}

	if (shortcut.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
		return (
			<ShortcutMenuItem
				key={shortcut.key}
				shortcut={shortcut}
				dragAndDropEnabled={enableDragAndDrop}
				status={status}
			/>
		);
	}

	return (
		<ShortcutMenuItem
			key={shortcut.key}
			shortcut={shortcut}
			status={status}
			forceStatic={forceStatic}
			product={product}
			openInSameTab={openInSameTab}
		/>
	);
}

const getFilteredProductLinks = functionWithFG(
	'navx-2972_fix_fhs_locking_problems',
	getFilteredProductLinksNew,
	getFilteredProductLinksOld,
);
